import { Component, ElementRef, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { HoldoverCarton, HoldoverCartonsApiService, HoldoverPreview, HoldoverResult, HoldoverSelection, ProductHistory } from './holdover-cartons-api.service';

interface CartonStoreGroup {
  key: string; label: string; storeNumber: string; customer: string;
  rows: HoldoverCarton[]; eligibleRows: HoldoverCarton[]; selected: number; cartonCount: number;
}

@Component({ selector: 'app-holdover-cartons', templateUrl: './holdover-cartons.component.html', styleUrls: ['./holdover-cartons.component.scss'] })
export class HoldoverCartonsComponent {
  pool = 'c9efdd22-94aa-4d92-8627-8c961ac3b26b';
  pools = [{ pool_point_uuid: this.pool, pool_point_name: 'FBI - Vaughan' }];
  rows: HoldoverCarton[] = [];
  selected = new Set<string>();
  counts: Record<string, number> = {};
  busy = false;
  loaded = false;
  apiCreateEnabled = false;
  search = '';
  category = 'ready';
  activeStore = '';
  showInvestigations = false;
  expandedStores = new Set<string>();
  page = 1;
  reason = '';
  releaseHolds = false;
  observedAt = '';
  error = '';
  preview: HoldoverPreview | null = null;
  reviewOpen = false;
  @ViewChild('reviewDialog') reviewDialog?: ElementRef<HTMLElement>;
  @ViewChild('previewButton') previewButton?: ElementRef<HTMLButtonElement>;
  result: HoldoverResult | null = null;
  historyRows: ProductHistory[] = [];
  historyBarcode = '';
  historyLoading = false;
  light = localStorage.getItem('holdover-theme') === 'light';
  private requestKey = '';
  readonly categories = [
    { key: 'ready', label: 'Ready' }, { key: 'held', label: 'Held' }, { key: 'needs_review', label: 'Needs review' }
  ];
  readonly investigationCategories = [
    { key: 'palletized', label: 'Already palletized' }, { key: 'excluded', label: 'Other exceptions' }
  ];
  constructor(private api: HoldoverCartonsApiService, public auth: AuthService) {}
  get canCreate() { return this.auth.hasPermission('holdover_cartons.create'); }
  get selectedRows() { return this.rows.filter(row => this.selected.has(row.product_uuid)); }
  get pendingCount() { return this.selectedRows.filter(row => row.pending_destination).length; }
  get heldCount() { return this.selectedRows.filter(row => row.held).length; }
  get holdReleaseReady() { return !this.heldCount || (this.releaseHolds && this.reason.trim().length >= 5); }
  get categoryRows() { return this.rows.filter(row => row.category === this.category); }
  get matchingRows() {
    const term = this.search.trim().toLowerCase();
    return this.categoryRows.filter(row => !term || [row.barcode, row.store_number, row.trip_number, row.source_asn, row.source_bol, row.customer_name].some(value => (value || '').toLowerCase().includes(term)));
  }
  get filtered() { return this.matchingRows.filter(row => !this.activeStore || this.storeKey(row) === this.activeStore); }
  get storeGroups() { return this.groupRows(this.matchingRows); }
  get filteredGroups() { return this.groupRows(this.filtered); }
  get visibleGroups() { return this.filteredGroups.slice((this.page - 1) * 25, this.page * 25); }
  get pageCount() { return Math.max(1, Math.ceil(this.filteredGroups.length / 25)); }
  get visibleCartonCount() { return new Set(this.filtered.map(row => row.barcode || row.product_uuid)).size; }
  get canSelectFiltered() { return this.filtered.some(row => row.eligible); }
  get viewNotice() {
    if (this.category === 'held') return 'Release held cartons with a reason. For cartons awaiting a destination, choose the store on the Honeywell and scan directly onto its pallet.';
    if (this.category === 'needs_review') return 'Cartons awaiting a destination can join the trip: choose their store on the Honeywell and scan onto its pallet. Duplicate labels and unresolved customers remain blocked.';
    if (this.category === 'palletized') return 'These cartons are already on pallets and cannot be added to a new loose-carton trip. Expand a store to investigate.';
    if (this.category === 'excluded') return 'These records have work-order, outbound, delivery or closed-status evidence. They are separate from the floor candidates and need investigation.';
    if (this.category === 'active_holdover') return 'These cartons already belong to holdover trips. Expand a store to open its trip and check progress.';
    return 'Ready cartons are selected. Expand a store to review its barcodes, or uncheck a store to leave its cartons out.';
  }
  private storeKey(row: HoldoverCarton) { return JSON.stringify([row.customer_name || '', row.store_number || '']); }
  private groupRows(rows: HoldoverCarton[]): CartonStoreGroup[] {
    const groups = new Map<string, CartonStoreGroup>();
    for (const row of rows) {
      const key = this.storeKey(row);
      let group = groups.get(key);
      if (!group) {
        const storeNumber = row.store_number || 'Unassigned';
        const customer = row.customer_name || 'Customer';
        group = { key, label: `${storeNumber} · ${customer}`, storeNumber, customer, rows: [], eligibleRows: [], selected: 0, cartonCount: 0 };
        groups.set(key, group);
      }
      group.rows.push(row);
      if (row.eligible) group.eligibleRows.push(row);
      if (this.selected.has(row.product_uuid)) group.selected++;
    }
    for (const group of groups.values()) group.cartonCount = new Set(group.rows.map(row => row.barcode || row.product_uuid)).size;
    return [...groups.values()];
  }
  chooseCategory(category: string) {
    this.category = category; this.activeStore = ''; this.search = ''; this.page = 1; this.expandedStores.clear();
  }
  toggleInvestigations() {
    this.showInvestigations = !this.showInvestigations;
    if (!this.showInvestigations && ['palletized', 'excluded'].includes(this.category)) this.chooseCategory('ready');
  }
  focusStore(key: string) { this.activeStore = key; this.page = 1; this.expandedStores.clear(); if (key) this.expandedStores.add(key); }
  toggleExpanded(key: string) { this.expandedStores.has(key) ? this.expandedStores.delete(key) : this.expandedStores.add(key); }
  isExpanded(key: string) { return this.expandedStores.has(key); }
  trackGroup(_index: number, group: CartonStoreGroup) { return group.key; }
  theme() { this.light = !this.light; localStorage.setItem('holdover-theme', this.light ? 'light' : 'dark'); }
  changed() { this.preview = null; this.requestKey = ''; this.error = ''; }
  filterChanged() {
    this.page = 1; this.activeStore = ''; this.expandedStores.clear();
    if (this.search.trim()) for (const group of this.storeGroups) this.expandedStores.add(group.key);
  }
  poolChanged() { this.rows = []; this.selected.clear(); this.loaded = false; this.result = null; this.chooseCategory('ready'); this.showInvestigations = false; this.changed(); }
  toggle(row: HoldoverCarton) {
    if (!row.eligible || this.busy) return;
    this.selected.has(row.product_uuid) ? this.selected.delete(row.product_uuid) : this.selected.add(row.product_uuid);
    this.changed();
  }
  toggleGroup(rows: HoldoverCarton[]) {
    if (this.busy || !this.canCreate) return;
    rows = rows.filter(row => row.eligible);
    const remove = rows.every(row => this.selected.has(row.product_uuid));
    for (const row of rows) remove ? this.selected.delete(row.product_uuid) : this.selected.add(row.product_uuid);
    this.changed();
  }
  selectVisible() { for (const row of this.filtered) if (row.eligible) this.selected.add(row.product_uuid); this.changed(); }
  clearSelection() { this.selected.clear(); this.changed(); }
  find() {
    this.busy = true; this.error = ''; this.preview = null; this.result = null; this.reviewOpen = false;
    this.api.candidates(this.pool).pipe(finalize(() => this.busy = false)).subscribe({
      next: response => {
        this.rows = response.data; this.counts = response.counts; this.pools = response.pools;
        this.observedAt = response.observed_at; this.apiCreateEnabled = response.permissions.create;
        this.loaded = true; this.requestKey = ''; this.chooseCategory('ready'); this.showInvestigations = false;
        this.selected = new Set(this.rows.filter(row => row.eligible && row.category === 'ready').map(row => row.product_uuid));
        this.releaseHolds = false;
      }, error: error => this.failed(error)
    });
  }
  private input(): HoldoverSelection {
    return { pool_point_uuid: this.pool, products: this.selectedRows.map(row => ({ product_uuid: row.product_uuid, fingerprint: row.fingerprint })), reason: this.reason, release_holds: this.releaseHolds };
  }
  review() {
    if (this.busy || !this.canCreate || !this.selected.size) return;
    const opening = !this.reviewOpen;
    this.reviewOpen = true; this.preview = null; this.requestKey = ''; this.error = '';
    if (opening) setTimeout(() => this.reviewDialog?.nativeElement.focus());
    if (!this.holdReleaseReady) return;
    this.busy = true;
    this.api.preview(this.input()).pipe(finalize(() => this.busy = false)).subscribe({
      next: value => {
        this.preview = value;
        this.apiCreateEnabled = value.permissions?.create ?? this.apiCreateEnabled;
        this.requestKey = Array.from(crypto.getRandomValues(new Uint8Array(24)), byte => byte.toString(16).padStart(2, '0')).join('');
      }, error: error => this.failed(error)
    });
  }
  closeReview() {
    if (this.busy) return;
    this.reviewOpen = false; this.changed();
    setTimeout(() => this.previewButton?.nativeElement.focus());
  }
  reviewKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') { event.preventDefault(); this.closeReview(); return; }
    if (event.key !== 'Tab') return;
    const dialog = this.reviewDialog?.nativeElement;
    const controls = dialog ? Array.from(dialog.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), [tabindex="0"]')) : [];
    if (!controls.length) { event.preventDefault(); return; }
    const first = controls[0], last = controls[controls.length - 1];
    if (event.shiftKey && (event.target === first || event.target === dialog)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (event.target === last || event.target === dialog)) { event.preventDefault(); first.focus(); }
  }
  create() {
    if (!this.preview || !this.requestKey || !this.apiCreateEnabled) return;
    this.busy = true; this.error = '';
    this.api.create(this.input(), this.requestKey).pipe(finalize(() => this.busy = false)).subscribe({ next: value => { this.result = value; this.preview = null; this.reviewOpen = false; }, error: error => this.failed(error) });
  }
  openTrip(uuid: string) {
    this.busy = true; this.error = '';
    this.api.detail(uuid).pipe(finalize(() => this.busy = false)).subscribe({ next: value => this.result = value, error: error => this.failed(error) });
  }
  history(row: HoldoverCarton) {
    this.historyBarcode = row.barcode; this.historyRows = []; this.historyLoading = true;
    this.api.history(row.product_uuid).pipe(finalize(() => this.historyLoading = false)).subscribe({ next: value => this.historyRows = value.data, error: error => this.failed(error) });
  }
  eventLabel(value: string) { return value.replace(/_/g, ' '); }
  trackRow(_index: number, row: HoldoverCarton) { return row.product_uuid; }
  private failed(error: HttpErrorResponse) { this.error = error.error?.message || error.error?.error || 'Request failed. Please try again.'; }
}
